import React from "react";
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { CARE_PROFILE_SECTION_IDS } from "../../../utils/careProfileSections";
import colors from "../../../assets/theme/colors";
import {
  SECTION_ICON_BY_ID,
  getToneStyle,
} from "./CareProfileOverviewCards";

const openSection = ({ section, onSectionSelect, onStepChange, onOpenCareTeam }) => {
  if (!section) return;

  if (section.targetStep) {
    if (onSectionSelect) {
      onSectionSelect(section);
      return;
    }
    onStepChange?.(section.targetStep);
    return;
  }

  if (section.id === CARE_PROFILE_SECTION_IDS.CARE_TEAM) {
    onOpenCareTeam?.();
  }
};

const ProfileSectionNavigatorChip = ({
  section,
  onSectionSelect,
  onStepChange,
  onOpenCareTeam,
}) => {
  const SectionIcon = SECTION_ICON_BY_ID[section.id] || DescriptionOutlinedIcon;
  const tone = getToneStyle(section.tone);
  const canOpen = Boolean(section.targetStep || (section.id === CARE_PROFILE_SECTION_IDS.CARE_TEAM && onOpenCareTeam));

  return (
    <Button
      type="button"
      disabled={!canOpen}
      onClick={() => openSection({ section, onSectionSelect, onStepChange, onOpenCareTeam })}
      sx={{
        flex: "0 0 auto",
        minWidth: { xs: 128, sm: 142 },
        maxWidth: { xs: 158, sm: 180 },
        minHeight: 58,
        px: 1,
        py: 0.85,
        borderRadius: "18px",
        border: `1px solid ${section.hasDetails ? alpha(tone.ink, 0.28) : colors.semantic.profileFlow.cardBorder}`,
        bgcolor: section.hasDetails ? alpha(tone.bg, 0.55) : colors.semantic.profileFlow.cardBg,
        color: colors.semantic.ink,
        textTransform: "none",
        justifyContent: "flex-start",
        opacity: 1,
        "&.Mui-disabled": { opacity: 1, color: colors.semantic.ink },
        "&:hover": {
          bgcolor: section.hasDetails ? alpha(tone.bg, 0.7) : alpha(tone.bg, 0.32),
          borderColor: alpha(tone.ink, 0.34),
        },
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", gap: 0.8, alignItems: "center", width: "100%" }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "12px",
            bgcolor: alpha(tone.bg, section.hasDetails ? 0.9 : 0.7),
            color: tone.ink,
            display: "grid",
            placeItems: "center",
            position: "relative",
            flexShrink: 0,
          }}
        >
          <SectionIcon sx={{ fontSize: 17 }} />
          {section.hasDetails ? (
            <Box
              sx={{
                position: "absolute",
                right: -3,
                bottom: -3,
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: colors.semantic.profileFlow.doneText,
                color: colors.semantic.profileFlow.cardBg,
                display: "grid",
                placeItems: "center",
                border: `2px solid ${colors.semantic.profileFlow.cardBg}`,
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 11 }} />
            </Box>
          ) : null}
        </Box>
        <Box sx={{ minWidth: 0, textAlign: "left" }}>
          <Typography noWrap sx={{ fontSize: "0.82rem", fontWeight: 850, lineHeight: 1.15, color: colors.semantic.ink }}>
            {section.label}
          </Typography>
          <Typography noWrap sx={{ mt: 0.18, fontSize: "0.68rem", fontWeight: 700, color: section.hasDetails ? colors.semantic.profileFlow.doneText : colors.semantic.profileFlow.notStartedText }}>
            {section.hasDetails ? "Added" : "Add when ready"}
          </Typography>
        </Box>
      </Box>
    </Button>
  );
};

const CareProfileSectionNavigator = ({
  sections = [],
  completionSummary,
  onSectionSelect,
  onStepChange,
  onOpenCareTeam,
}) => {
  if (!sections.length || !completionSummary) return null;

  return (
    <Box
      sx={{
        p: { xs: 1.15, sm: 1.3 },
        borderRadius: "24px",
        border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
        bgcolor: alpha(colors.semantic.profileFlow.cardBg, 0.94),
        boxShadow: `0 6px 16px ${alpha(colors.semantic.ink, 0.035)}`,
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 950, letterSpacing: "0.1em", textTransform: "uppercase", color: colors.semantic.profileFlow.eyebrowText }}>
              Profile progress
            </Typography>
            <Typography sx={{ mt: 0.15, fontSize: "0.82rem", color: colors.semantic.ink2 }}>
              Swipe or tap a section to jump there.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 850, color: colors.semantic.profileFlow.completionText, whiteSpace: "nowrap" }}>
            {completionSummary.label}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={completionSummary.percent}
          sx={{
            height: 7,
            borderRadius: 999,
            bgcolor: colors.semantic.profileFlow.progressEmpty,
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              bgcolor: colors.semantic.profileFlow.progressFilled,
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            overflowX: "auto",
            pb: 0.15,
            mx: -0.25,
            px: 0.25,
            scrollSnapType: "x proximity",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            "& > *": { scrollSnapAlign: "start" },
          }}
        >
          {sections.map((section) => (
            <ProfileSectionNavigatorChip
              key={section.id}
              section={section}
              onSectionSelect={onSectionSelect}
              onStepChange={onStepChange}
              onOpenCareTeam={onOpenCareTeam}
            />
          ))}
        </Box>
      </Stack>
    </Box>
  );
};

export default CareProfileSectionNavigator;
